import { AnomalyAPI, convertSecondsToMilliseconds, duration, Nullable } from '@shared';
import { find, includes, pipe, tail, times } from 'ramda';
import { Tick } from './EnhancedTimeline.types';

export const getAnomalyPosition = (
  videoDuration: number,
  start: number,
  end: number,
  inSeconds = false
) => {
  const convertedStart = inSeconds ? convertSecondsToMilliseconds(start) : start;
  const convertedEnd = inSeconds ? convertSecondsToMilliseconds(end) : end;

  let left = (convertedStart / videoDuration) * 100;
  let width = (duration(convertedStart, convertedEnd) / videoDuration) * 100;

  if (left < 0) {
    left = 0;
  }
  const maxWidth = 100 - left;
  if (width > maxWidth) {
    width = maxWidth;
  }

  return { left, width };
};

const ANOMALY_ORIGIN_TYPES = {
  screen: [11, 12, 13, 14],
  camera: [5, 7, 8, 9, 10],
  sound: [6],
};

export function anomaliesByOriginType(
  anomalies: Nullable<AnomalyAPI[]>,
  originType: 'screen' | 'camera' | 'sound'
) {
  return anomalies?.filter((anomaly) => {
    const anomalyTypeId = anomaly.anomalyType.id;

    if (originType === 'screen' && includes(anomalyTypeId, ANOMALY_ORIGIN_TYPES.screen)) {
      return true;
    }

    if (originType === 'sound' && includes(anomalyTypeId, ANOMALY_ORIGIN_TYPES.sound)) {
      return true;
    }

    if (originType === 'camera') {
      return (
        !includes(anomalyTypeId, ANOMALY_ORIGIN_TYPES.screen) &&
        !includes(anomalyTypeId, ANOMALY_ORIGIN_TYPES.sound)
      );
    }

    return false;
  });
}

const TICK_INTERVALS = [
  {
    value: 10000, // 10 sec
    durationFrom: 0,
    durationTo: 60000, // 1 min
  },
  {
    value: 20000, // 20 sec
    durationFrom: 60000, // 1 min
    durationTo: 300000, // 5 min
  },
  {
    value: 60000, // 1 min
    durationFrom: 300000, // 5 min
    durationTo: 1200000, // 20 min
  },
  {
    value: 600000, // 10 min
    durationFrom: 1200000, // 20 min
    durationTo: 3600000, // 1 h
  },
  {
    value: 1200000, // 20 min
    durationFrom: 3600000, // 1 h
    durationTo: 7200000, // 2 h
  },
  {
    value: 1800000, // 30 min
    durationFrom: 7200000, // 2 h
    durationTo: 43200000, // 12 h
  },
  {
    value: 3600000, // 1 h
    durationFrom: 43200000, // 12 h
    durationTo: 86400000, // 24 h
  },
];

const isBetween = (from: number, current: number, to: number) => {
  return from <= current && current < to;
};

export const getTicks = (videoDuration: number) => {
  const tickInterval = find(
    ({ durationFrom, durationTo }) => isBetween(durationFrom, videoDuration, durationTo),
    TICK_INTERVALS
  );

  if (!tickInterval) {
    return [];
  }

  let tickValue = tickInterval.value;

  let ticksCount = Math.round(videoDuration / tickValue);

  // responsive ticks
  const TICK_WIDTH = 53;
  const containerWidth = document.getElementById('scheme-container')?.clientWidth;
  const oldTicksCount = ticksCount;
  if (containerWidth && ticksCount * TICK_WIDTH > containerWidth) {
    do {
      ticksCount -= 1;
    } while (containerWidth && ticksCount * TICK_WIDTH > containerWidth);
    const ratio = oldTicksCount / ticksCount - 1;
    tickValue += tickValue * ratio;
  }

  const getTick = (index: number) => {
    const timestamp = tickValue * index;

    return {
      id: index,
      timestamp,
      left: (timestamp / videoDuration) * 100,
    };
  };

  return pipe(times(getTick), tail)(ticksCount) as Tick[];
};
